/**
 * Light palette
 *
 * @type {typeof palette}
 */
const palette = {
  primary: {
    500: '#3B5FF1',
    400: '#5468FF',
    300: '#899FF7',
    200: '#B1BFF9',
    100: '#D8DFFC',
  },
  secondary: {
    500: '#344356',
    400: '#506784',
    300: '#748CAB',
    200: '#A2B2C7',
    100: '#D1D9E3',
  },
  neutral: {
    500: '#E1E5EE',
    400: '#EDEFF5',
    300: '#F3F5F8',
    200: '#F9FAFC',
    100: '#FFFFFF',
  },
  warning: {
    500: '#FFCC3E',
    400: '#FFD665',
    300: '#FFE08B',
    200: '#FFEBB2',
    100: '#FFF5D8',
  },
  danger: {
    500: '#E45851',
    400: '#E97974',
    300: '#EF9B97',
    200: '#F4BCB9',
    100: '#FADEDC',
  },
  success: {
    500: '#62CA76',
    400: '#81D591',
    300: '#A1DFAD',
    200: '#C0EAC8',
    100: '#E0F4E4',
  },
};

/**
 * Light theme
 *
 * @type { import("@pulsar/core").Theme }
 */
const theme = {
  breakpoints: {
    phone: 320,
    tablet: 768,
    desktop: 1280,
  },
  fontSizes: {
    0: 12,
    1: 14,
    2: 16,
    3: 18,
    4: 20,
    5: 24,
    6: 32,
    // @ts-ignore
    body: 16,
  },
  space: {
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 32,
    6: 64,
    ios: 12,
    android: 16,
    web: 16,
    default: 16,
  },
  radii: {
    0: 0,
    1: 4,
    2: 8,
    3: 10,
    4: 12,
    5: 16,
    ios: 10,
    android: 8,
    web: 8,
    default: 8,
  },
  shadows: {
    s: {
      shadowColor: 'rgba(0, 0, 0, 0.08)',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 1,
      shadowRadius: 4,

      elevation: 2,
    },
    m: {
      shadowColor: 'rgba(0, 0, 0, 0.08)',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 1,
      shadowRadius: 24,

      elevation: 4,
    },
    l: {
      shadowColor: 'rgba(0, 0, 0, 0.16)',
      shadowOffset: {
        width: 0,
        height: 0,
      },
      shadowOpacity: 1,
      shadowRadius: 96,

      elevation: 8,
    },
    xl: {
      shadowColor: 'rgba(0, 0, 0, 0.16)',
      shadowOffset: {
        width: 0,
        height: 0,
      },
      shadowOpacity: 1,
      shadowRadius: 96,

      elevation: 16,
    },
  },
  colors: {
    textDefault: palette.secondary[500],
    textSecondary: palette.secondary[300],
    linkDefault: palette.primary[500],
    hoverableBG: 'rgba(0, 0, 0, 0.04)',
    buttonPrimaryBG: palette.primary[500],
    buttonSecondaryBG: palette.neutral[300],
    buttonTertiaryBG: 'rgba(0, 0, 0, .0)',
    buttonPrimaryText: palette.neutral[100],
    buttonSecondaryText: palette.primary[500],
    buttonTertiaryText: palette.primary[500],
    buttonOutlineBG: palette.neutral[100],
    buttonOutlineText: palette.primary[500],
    buttonOutlineBorder: palette.primary[500],
    separator: palette.neutral[500],
    headerControl: palette.primary[500],
    counterPrimaryBG: palette.primary[500],
    counterPrimaryText: palette.neutral[100],
    counterProminentBG: palette.danger[500],
    counterProminentText: palette.neutral[100],
    counterSecondaryBG: palette.secondary[200],
    counterSecondaryText: palette.neutral[100],
    formFieldBorder: palette.neutral[500],
    formFieldHoveredBorder: palette.primary[200],
    formFieldFocusedBorder: palette.primary[500],
    formFieldBG: palette.neutral[200],
    formFieldPlaceholder: palette.secondary[300],
    formFieldTopText: palette.secondary[400],
    formFieldBottomText: palette.secondary[500],
    formFieldErrorBG: palette.danger[100],
    formFieldErrorBorder: palette.danger[500],
    formFieldErrorText: palette.danger[500],
    formFieldSuccessBG: palette.neutral[200],
    formFieldSuccessBorder: palette.success[500],
    formFieldSuccessText: palette.secondary[500],
    formFieldIcon: palette.secondary[200],
    formFieldIconActive: palette.primary[400],
    searchBarBG: palette.neutral[500],
    searchBarPlaceholder: palette.secondary[300],
    searchBarIcon: palette.secondary[300],
    tabBarInactive: palette.secondary[200],
    tabBarActive: palette.primary[500],
    sheetBG: palette.neutral[100],
    alertBG: palette.neutral[100],
    alertDestructiveActionText: palette.danger[500],
    alertActionText: palette.primary[500],
    iconPrimary: palette.primary[500],
    iconSecondary: palette.secondary[200],
    cardBG: palette.neutral[100],
    cardBorder: palette.neutral[400],
    switchThumbIOS: palette.neutral[100],
    switchTrackIOSOn: palette.primary[500],
    switchTrackIOSOff: palette.neutral[400],
    switchThumb: palette.neutral[100],
    switchThumbActive: palette.primary[500],
    switchTrackOn: palette.primary[300],
    switchTrackOff: palette.neutral[400],
    avatarBG: palette.neutral[300],
    spinner: palette.primary[500],
  },
  fontWeights: {
    regular: {
      fontFamily: 'NotoSans-Regular',
      fontWeight: '400',
    },
    medium: {
      fontFamily: 'NotoSans-Medium',
      fontWeight: '500',
    },
    semibold: {
      fontFamily: 'NotoSans-SemiBold',
      fontWeight: '600',
    },
    bold: {
      fontFamily: 'NotoSans-Bold',
      fontWeight: '700',
    },
  },
  fonts: {
    title1: {
      fontSize: 24,
      lineHeight: 28,
    },
    title2: {
      fontSize: 20,
      lineHeight: 24,
    },
    title3: {
      fontSize: 17,
      lineHeight: 22,
    },
    headline: {
      fontSize: 16,
      lineHeight: 20,
    },
    text: {
      fontSize: 15,
      lineHeight: 20,
    },
    subheading: {
      fontSize: 14,
      lineHeight: 18,
    },
    caption1: {
      fontSize: 13,
      lineHeight: 16,
    },
    caption2: {
      fontSize: 12,
      lineHeight: 14,
    },
    caption3: {
      fontSize: 11,
      lineHeight: 14,
    },
    caption4: {
      fontSize: 9,
      lineHeight: 12,
    },
  },
};

module.exports = { theme, palette };
